import { useState } from 'react';
import { BUSINESS, contactRequestSchema } from '@mopani/contracts';
import { Hero, Section } from '../components/ui/primitives';
import { Field } from '../components/ui/Field';
import { ApiRequestError, submitContact } from '../lib/api';
import { usePageMeta } from '../lib/seo';

/**
 * Contact. Baseline §3 — navy hero (204px, H1 48px) → muted contact block with
 * four cards (Business Hours is a navy card among white ones — P-18) and the
 * "Send a Message" form → white "Our Delivery Footprint".
 *
 * CORRECTED here: phone (C-1), WhatsApp deep link (C-2), email (C-3), named
 * contact person (C-5). All from the single BUSINESS constant.
 *
 * ADDED: the form actually submits (C-9 / A-2). In production it submitted
 * nowhere AND no contact endpoint existed at all.
 */

type Errors = Partial<Record<'fullName' | 'email' | 'phone' | 'message' | 'form', string>>;

export function Contact() {
  usePageMeta({
    title: 'Contact Us',
    description: `Contact Mopani Suppliers in Phalaborwa — call or WhatsApp ${BUSINESS.phoneDisplay}, or email ${BUSINESS.email}. Serving schools and institutions across the Mopani District.`,
    path: '/contact',
  });

  const [errors, setErrors] = useState<Errors>({});
  const [status, setStatus] = useState<'idle' | 'submitting' | 'sent'>('idle');

  async function onSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrors({});
    const fd = new FormData(e.currentTarget);
    const input = {
      fullName: String(fd.get('fullName') ?? ''),
      email: String(fd.get('email') ?? ''),
      phone: String(fd.get('phone') ?? ''),
      message: String(fd.get('message') ?? ''),
      website: String(fd.get('website') ?? ''), // honeypot (A-11)
    };

    const parsed = contactRequestSchema.safeParse(input);
    if (!parsed.success) {
      const flat = parsed.error.flatten().fieldErrors;
      setErrors({
        fullName: flat.fullName?.[0],
        email: flat.email?.[0],
        phone: flat.phone?.[0],
        message: flat.message?.[0],
      });
      return;
    }

    setStatus('submitting');
    try {
      await submitContact(parsed.data);
      setStatus('sent');
    } catch (err) {
      setStatus('idle');
      const msg =
        err instanceof ApiRequestError
          ? err.message
          : 'Something went wrong sending your message.';
      setErrors({ form: `${msg} You can also reach us on ${BUSINESS.phoneDisplay}.` });
    }
  }

  return (
    <>
      <Hero
        title="Contact Us"
        subtitle="Get in touch for general enquiries, supplier information, or support."
        size="sm"
        padding="pt-16 pb-12"
      />

      <section className="section section-muted">
        <div className="container-content grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          {/* Contact cards */}
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-1">
            <div className="card p-6">
              <h2 className="font-heading text-lg font-bold text-navy">Phone / WhatsApp</h2>
              <a
                href={`tel:${BUSINESS.phone}`}
                className="mt-2 block text-lg font-medium text-charcoal/80 hover:text-green-accent"
              >
                {BUSINESS.phoneDisplay}
              </a>
              <a
                href={BUSINESS.whatsappUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-green-accent hover:underline"
              >
                Message us on WhatsApp
              </a>
              <p className="mt-2 text-sm text-charcoal/60">Available during business hours</p>
              <p className="mt-3 text-sm text-charcoal/80">
                Speak to <strong className="font-semibold">{BUSINESS.contactName}</strong> ·{' '}
                {BUSINESS.contactTitle}
              </p>
            </div>

            <div className="card p-6">
              <h2 className="font-heading text-lg font-bold text-navy">Email Address</h2>
              <a
                href={`mailto:${BUSINESS.email}`}
                className="mt-2 block text-charcoal/80 hover:text-green-accent"
              >
                {BUSINESS.email}
              </a>
            </div>

            <div className="card p-6">
              <h2 className="font-heading text-lg font-bold text-navy">Location</h2>
              <p className="mt-2 text-charcoal/80">
                {BUSINESS.town}
                <br />
                {BUSINESS.province}, {BUSINESS.postalCode}
              </p>
            </div>

            {/* Navy card among white ones — preserved from production (P-18). */}
            <div className="rounded-lg bg-navy p-6 text-white">
              <h2 className="font-heading text-lg font-bold text-white">Business Hours</h2>
              <dl className="mt-3 space-y-1 text-sm">
                {BUSINESS.hours.map((h) => (
                  <div key={h.days} className="flex justify-between gap-4">
                    <dt className="text-white/70">{h.days}:</dt>
                    <dd className="text-white">
                      {h.open ? `${h.open} - ${h.close}` : 'Closed'}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>

          {/* Send a Message */}
          <div className="card p-8">
            <h2 className="font-heading text-2xl font-bold text-navy">Send a Message</h2>
            <p className="mt-2 text-sm text-charcoal/70">
              For quote requests, please use our{' '}
              <a href="/quote" className="font-semibold text-green-accent hover:underline">
                dedicated quote form
              </a>
              .
            </p>

            {status === 'sent' ? (
              <div
                className="mt-8 rounded-lg border border-green-cta/40 bg-green-cta/5 p-6"
                role="status"
              >
                <h3 className="font-heading text-lg font-bold text-navy">Message sent</h3>
                <p className="mt-2 text-charcoal/80">
                  Thank you — we have received your message and will respond during business hours.
                  If it is urgent, call or WhatsApp us on {BUSINESS.phoneDisplay}.
                </p>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="mt-6 space-y-5">
                {errors.form && (
                  <div className="field-error rounded border border-[#c0392b]/30 bg-[#c0392b]/5 p-3" role="alert">
                    {errors.form}
                  </div>
                )}

                <Field name="fullName" label="Full Name" required error={errors.fullName}>
                  {(p) => <input type="text" autoComplete="name" {...p} />}
                </Field>

                <Field name="email" label="Email Address" required error={errors.email}>
                  {(p) => <input type="email" autoComplete="email" {...p} />}
                </Field>

                <Field name="phone" label="Phone Number" error={errors.phone}>
                  {(p) => <input type="tel" autoComplete="tel" placeholder="063 996 0861" {...p} />}
                </Field>

                <Field name="message" label="Message" required error={errors.message}>
                  {(p) => <textarea rows={5} {...p} />}
                </Field>

                {/* Honeypot — the backend already implements this trap (A-11). */}
                <div className="hidden" aria-hidden="true">
                  <label htmlFor="field-website">Leave this field empty</label>
                  <input id="field-website" name="website" type="text" tabIndex={-1} autoComplete="off" />
                </div>

                <button
                  type="submit"
                  className="btn btn-primary btn-hero w-full"
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? 'Sending…' : 'Send Message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      <Section
        variant="white"
        title="Our Delivery Footprint"
        intro="We focus our delivery services directly to institutions within the Mopani district to ensure reliable, timely supply."
      >
        <ul className="flex flex-wrap gap-3">
          {BUSINESS.serviceAreas.map((a) => (
            <li
              key={a}
              className="rounded-full border border-border bg-muted px-4 py-2 text-sm font-medium text-charcoal"
            >
              {a}
            </li>
          ))}
        </ul>
      </Section>
    </>
  );
}
